import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { Spinner, ErrorState, EmptyState } from '@/components/common/State'

const BAND_BG: Record<string, string> = {
  critical: 'bg-error text-white',
  high: 'bg-error/40 text-white',
  medium: 'bg-yellow-400/70 text-neutral',
  low: 'bg-secondary/50 text-primary',
  info: 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark',
}

// Asset × framework band-coloured grid — gap-at-glance regulator lens (design §5.8, Compliance).
export function HeatmapLens({ orgId }: { orgId: string }) {
  const heatmap = useQuery({
    queryKey: ['crosswalk-heatmap', orgId],
    queryFn: () => analyticsApi.crosswalkHeatmap(orgId),
    enabled: !!orgId,
  })

  const { assets, frameworks, grid } = useMemo(() => {
    // Backend returns a FLAT HeatmapCell[]; derive the asset rows and framework
    // columns from it (unique, order-preserving), plus a lookup grid.
    const cells = Array.isArray(heatmap.data) ? heatmap.data : []
    const assetMap = new Map<string, { id: string; name: string }>()
    const fwSet = new Set<string>()
    const grid = new Map<string, { band: string; applies: boolean }>()
    for (const c of cells) {
      if (!assetMap.has(c.asset_id)) assetMap.set(c.asset_id, { id: c.asset_id, name: c.asset_name })
      fwSet.add(c.framework)
      grid.set(`${c.asset_id}::${c.framework}`, { band: c.band, applies: c.in_scope })
    }
    return { assets: [...assetMap.values()], frameworks: [...fwSet].sort(), grid }
  }, [heatmap.data])

  if (heatmap.isLoading) return <Spinner />
  if (heatmap.error) return <ErrorState error={heatmap.error} />
  if (assets.length === 0 || frameworks.length === 0)
    return (
      <EmptyState
        icon="grid_on"
        title="No framework coverage data"
        hint="Requires scored assets in scope of a regulatory framework."
      />
    )

  return (
    <div className="overflow-auto">
      <table className="border-separate border-spacing-1 text-xs">
        <thead>
          <tr>
            <th className="text-left px-2 py-1 text-subtle-light dark:text-subtle-dark">Asset</th>
            {frameworks.map((f) => (
              <th key={f} className="px-2 py-1 text-subtle-light dark:text-subtle-dark whitespace-nowrap">
                {f}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {assets.map((a) => (
            <tr key={a.id}>
              <td className="px-2 py-1 font-medium text-text-light dark:text-text-dark whitespace-nowrap max-w-[220px] truncate">
                {a.name}
              </td>
              {frameworks.map((f) => {
                const cell = grid.get(`${a.id}::${f}`)
                const inScope = cell?.applies
                return (
                  <td key={f} className="p-0">
                    <div
                      className={`w-16 h-8 rounded flex items-center justify-center ${
                        inScope ? BAND_BG[cell!.band] ?? BAND_BG.info : 'bg-transparent opacity-40'
                      }`}
                      title={inScope ? cell!.band : 'not in scope'}
                    >
                      {inScope ? cell!.band[0].toUpperCase() : '·'}
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
